import { TicketIcon } from '@heroicons/react/outline';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button, PageLoader as Loader } from '@cfm-media/pwa-ui-components';
import { getVacancy } from '../stores/flows/upgrade';
import { observer } from 'mobx-react-lite';

export const ConfirmBookingModal = observer(
  ({
    onClose = () => null,
    setShow = () => null,
    bookingConfig,
    show = false,
    fullWidth,
  }) => {
    const [active, setActive] = useState(true);
    const [notAvailable, setNotAvailable] = useState(false);
    const [available, setAvailable] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [newPrice, setNewPrice] = useState(null);
    const [currency, setCurrency] = useState('EUR');

    useEffect(() => {
      async function fetchData() {
        if (show) {
          let vacancyRes = await checkVacancy(bookingConfig);

          setTimeout(() => {
            if (vacancyRes?.success === false) {
              setFetchError(true);
              setActive(false);
              return;
            }
            if (vacancyRes?.isAvailable === true) {
              setNewPrice(parseInt(vacancyRes?.price));
              setCurrency(vacancyRes?.currency);
              setActive(false);
              setAvailable(true);
            } else {
              setNotAvailable(true);
              setActive(false);
            }
          }, 1000);
        }
      }
      fetchData();
    }, [show]);

    return (
      <Transition.Root show={show} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShow}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div
            className={classNames(
              'fixed z-10 inset-0 overflow-y-auto pb-24 pt-20 px-6 lg:py-10',
              {
                'lg:pl-[300px]': !fullWidth,
              },
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="flex items-center min-h-full">
                <Dialog.Panel className="bg-white px-4 pt-5 pb-4 mx-auto relative overflow-hidden rounded-lg shadow-xl transition-all sm:max-w-md sm:p-6">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <TicketIcon
                      className="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      {fetchError
                        ? 'Ein Fehler ist aufgetreten.'
                        : 'Buchung bestätigen?'}
                    </Dialog.Title>

                    <div className="mt-2">
                      <div className="mb-4">
                        Sie sind dabei, die folgende Leistung zu buchen:
                      </div>
                      <div className="mb-4">
                        <strong>{bookingConfig.name}</strong>
                      </div>

                      {active ? (
                        <Loader
                          variant="static"
                          info="Verfügbarkeit wird geprüft"
                          shadow="shadow-none"
                        />
                      ) : (
                        <div className="text-sm text-gray-500">
                          {fetchError
                            ? 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
                            : ''}
                        </div>
                      )}
                    </div>

                    {notAvailable ? (
                      <div className="mb-2 text-brand-1 font-semibold text-xs">
                        Die gewählte Leistung ist aktuell nicht verfügbar.
                      </div>
                    ) : null}

                    {available ? (
                      <>
                        <div className="mb-4">
                          Bitte bestätigen Sie die Buchung, um fortzufahren.
                        </div>

                        <div className="mb-2 text-brand-1 font-semibold text-xs">
                          Die gewählte Leistung ist verfügbar.
                          <br />
                          <br />
                          Aktueller Preis:{' '}
                          <strong>
                            {newPrice - bookingConfig.originalPrice},-
                            {currency}
                          </strong>
                        </div>
                      </>
                    ) : null}

                    {fetchError ? (
                      <div className="mb-2 text-brand-1 font-semibold text-xs">
                        Ein Fehler ist aufgetreten. Bitte versuchen Sie es
                        später erneut.
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-5 sm:mt-6">
                    <Button
                      variant="primary"
                      disabled={fetchError || !available}
                      block
                      onButtonClick={() => {
                        onClose();
                        setShow(false);
                      }}
                      title="Buchung bestätigen"
                    />

                    <Button
                      className="mt-3"
                      onButtonClick={() => {
                        setNotAvailable(false);
                        setAvailable(false);
                        setFetchError(false);
                        setShow(false);
                        setActive(true);
                      }}
                      title="Abbrechen"
                      block
                    />
                  </div>
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    );
  },
);

async function checkVacancy(bookingConfig) {
  try {
    const res = await getVacancy(bookingConfig);
    if (res?.success === false) {
      return res;
    }

    if (res?.status === 'J') {
      return { isAvailable: true, price: res?.price };
    } else {
      return { isAvailable: false };
    }
  } catch (error) {
    console.log(error);
    return { success: false };
  }
}
